<template>
  <div>
    <Navigation />
    <div class="wrap" v-if="kpi">
      <!-- ONBOARDING WIZARD -->
      <div v-if="onboarding && !onboarding.dismissed && !onboarding.completed_at" class="wizard card">
        <div class="wiz-head">
          <div>
            <div class="wiz-eyebrow">Get started</div>
            <h3>Welcome to Paysol, {{ userName }}!</h3>
            <p class="muted">A few quick steps to set up your business. Takes about 5 minutes.</p>
          </div>
          <button class="secondary small" @click="dismissWiz">Skip for now</button>
        </div>
        <div class="wiz-progress">
          <div class="wiz-bar"><div class="wiz-fill" :style="{ width: (onboarding.required_done / Math.max(1, onboarding.required_total) * 100) + '%' }"></div></div>
          <span class="wiz-progress-text">{{ onboarding.required_done }} of {{ onboarding.required_total }} required steps done</span>
        </div>
        <div class="wiz-steps">
          <div v-for="s in onboarding.steps" :key="s.key" :class="{ 'wiz-step': 1, done: s.done, optional: !s.required }">
            <div class="wiz-check">
              <i class="mdi" :class="s.done ? 'mdi-check-circle' : 'mdi-circle-outline'"></i>
            </div>
            <div class="wiz-step-body">
              <div class="wiz-step-title">
                {{ s.label }}
                <span v-if="!s.required" class="wiz-opt">optional</span>
              </div>
              <div class="wiz-step-cta" v-if="!s.done">
                <router-link :to="wizLink(s.key)">Do this now →</router-link>
              </div>
              <div v-else class="wiz-step-done">✓ done</div>
            </div>
          </div>
        </div>
        <div class="wiz-actions" v-if="onboarding.ready_to_complete">
          <button class="primary" @click="finishWiz">
            <i class="mdi mdi-check-circle"></i> Mark onboarding complete
          </button>
        </div>
      </div>

      <div class="head">
        <div>
          <h1>Dashboard</h1>
          <p class="lede">{{ kpi.tenant.name }} · {{ prettyDate() }}</p>
        </div>
        <div class="head-actions">
          <button class="secondary" @click="load"><i class="mdi mdi-refresh"></i> Refresh</button>
        </div>
      </div>

      <!-- KPI TILES -->
      <div class="kpi-grid">
        <div class="kpi teal">
          <div class="kpi-label">Today</div>
          <div class="kpi-val">KES {{ format(kpi.today.gross) }}</div>
          <div class="kpi-sub">{{ kpi.today.n }} sale{{ kpi.today.n === 1 ? '' : 's' }} · Discounts: KES {{ format(kpi.today.discount) }}</div>
        </div>
        <div class="kpi">
          <div class="kpi-label">This week</div>
          <div class="kpi-val">KES {{ format(kpi.week.gross) }}</div>
          <div class="kpi-sub">{{ kpi.week.n }} sales</div>
        </div>
        <div class="kpi">
          <div class="kpi-label">This month</div>
          <div class="kpi-val">KES {{ format(kpi.month.gross) }}</div>
          <div class="kpi-sub">{{ kpi.month.n }} sales</div>
        </div>
        <div class="kpi" :class="{ warn: kpi.open_tills > 0 }">
          <div class="kpi-label">Open tills</div>
          <div class="kpi-val">{{ kpi.open_tills }}</div>
          <div class="kpi-sub">Currently taking payments</div>
        </div>
      </div>

      <!-- QUICK ACTIONS -->
      <div class="quick-row">
        <router-link to="/pos"><button class="primary"><i class="mdi mdi-cash-register"></i> Open POS</button></router-link>
        <router-link v-if="isRestaurantMode" to="/tables"><button class="secondary"><i class="mdi mdi-table-furniture"></i> Tables</button></router-link>
        <router-link v-if="isServiceMode" to="/appointments"><button class="secondary"><i class="mdi mdi-calendar-check"></i> Appointments</button></router-link>
        <router-link to="/products"><button class="secondary"><i class="mdi mdi-package-variant"></i> Products</button></router-link>
        <router-link to="/reports"><button class="secondary"><i class="mdi mdi-chart-line"></i> Reports</button></router-link>
      </div>

      <!-- 7-DAY TREND -->
      <div class="card trend-card">
        <div class="section-head">
          <h3>Last 7 days</h3>
          <div class="muted small">Total: KES {{ format(trendTotal) }}</div>
        </div>
        <div class="trend-chart">
          <div v-for="(d, i) in kpi.trend_7d" :key="i" class="bar-col">
            <div class="bar-wrap">
              <div class="bar" :style="{ height: barHeight(d.gross) + '%' }" :title="`KES ${format(d.gross)}`"></div>
            </div>
            <div class="bar-label">{{ shortDay(d.date) }}</div>
            <div class="bar-value muted small">{{ d.gross > 0 ? formatK(d.gross) : '—' }}</div>
          </div>
        </div>
      </div>

      <!-- 2-COL GRID -->
      <div class="two-col">
        <!-- Payment method breakdown today -->
        <div class="card">
          <div class="section-head">
            <h3>Payments today</h3>
            <div class="muted small">by method</div>
          </div>
          <div v-if="kpi.by_method.length" class="method-list">
            <div v-for="m in kpi.by_method" :key="m.method" class="method-row">
              <div class="method-name">{{ prettyMethod(m.method) }}</div>
              <div class="method-bar-wrap">
                <div class="method-bar" :style="{ width: methodPct(m.total) + '%', background: methodColor(m.method) }"></div>
              </div>
              <div class="method-val">KES {{ format(m.total) }}</div>
            </div>
          </div>
          <p v-else class="muted small center">No payments recorded yet today.</p>
        </div>

        <!-- Top sellers today -->
        <div class="card">
          <div class="section-head">
            <h3>Top sellers today</h3>
            <div class="muted small">by units</div>
          </div>
          <table v-if="kpi.top_items.length" class="mini-table">
            <thead><tr><th>#</th><th>Item</th><th class="r">Units</th><th class="r">Revenue</th></tr></thead>
            <tbody>
              <tr v-for="(t, i) in kpi.top_items" :key="i">
                <td class="muted">{{ i + 1 }}</td>
                <td>{{ t.name }}</td>
                <td class="r"><strong>{{ Math.round(t.qty) }}</strong></td>
                <td class="r">KES {{ format(t.revenue) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small center">No sales yet today.</p>
        </div>

        <!-- Revenue by hour -->
        <div class="card">
          <div class="section-head">
            <h3>Sales by hour</h3>
            <div class="muted small">today (KES)</div>
          </div>
          <div class="hour-chart">
            <div v-for="h in 24" :key="h" class="hour-col">
              <div class="hour-bar-wrap">
                <div v-if="kpi.by_hour[h-1] > 0" class="hour-bar" :style="{ height: hourHeight(kpi.by_hour[h-1]) + '%' }" :title="`${h-1}:00 · KES ${format(kpi.by_hour[h-1])}`"></div>
              </div>
              <div v-if="(h-1) % 3 === 0" class="hour-label muted small">{{ (h-1).toString().padStart(2,'0') }}</div>
            </div>
          </div>
        </div>

        <!-- Cashier leaderboard -->
        <div class="card">
          <div class="section-head">
            <h3>Cashier leaderboard</h3>
            <div class="muted small">today</div>
          </div>
          <table v-if="kpi.cashiers.length" class="mini-table">
            <thead><tr><th>Cashier</th><th class="r">Sales</th><th class="r">Gross</th><th class="r">Voids</th></tr></thead>
            <tbody>
              <tr v-for="c in kpi.cashiers" :key="c.cashier">
                <td>{{ c.cashier }}</td>
                <td class="r">{{ c.n_sales }}</td>
                <td class="r"><strong>KES {{ format(c.gross) }}</strong></td>
                <td class="r" :class="{ warn: c.n_voids > 0 }">{{ c.n_voids }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small center">No cashier activity yet today.</p>
        </div>

        <!-- Low stock -->
        <div class="card">
          <div class="section-head">
            <h3>Reorder soon</h3>
            <div class="muted small">low stock ({{ kpi.low_stock.length }})</div>
          </div>
          <table v-if="kpi.low_stock.length" class="mini-table">
            <thead><tr><th>Item</th><th class="r">On hand</th><th class="r">Reorder pt</th></tr></thead>
            <tbody>
              <tr v-for="(l, i) in kpi.low_stock" :key="i">
                <td>{{ l.product_name }} · <span class="muted small">{{ l.variant_name }}</span></td>
                <td class="r warn"><strong>{{ l.on_hand }}</strong></td>
                <td class="r muted">{{ l.reorder_point }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small center">Everything is well-stocked. ✓</p>
        </div>

        <!-- Recent sales -->
        <div class="card">
          <div class="section-head">
            <h3>Recent sales</h3>
            <router-link to="/reports" class="muted small">All reports →</router-link>
          </div>
          <table v-if="kpi.recent.length" class="mini-table">
            <thead><tr><th>Receipt</th><th>Cashier</th><th class="r">Total</th><th>When</th></tr></thead>
            <tbody>
              <tr v-for="r in kpi.recent" :key="r.id">
                <td><code>{{ r.receipt_no }}</code></td>
                <td class="muted small">{{ r.cashier || '—' }}</td>
                <td class="r">KES {{ format(r.total) }}</td>
                <td class="muted small">{{ shortTime(r.created_at) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small center">No sales yet.</p>
        </div>
      </div>
    </div>
    <div v-else class="loader">Loading…</div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

const METHOD_LABEL = {
  cash: 'Cash', mpesa_stk: 'M-Pesa STK', mpesa_till: 'M-Pesa (till)',
  card: 'Card', wallet: 'Wallet', store_credit: 'Store credit', bank_transfer: 'Bank transfer',
};
const METHOD_COLOR = {
  cash: '#4FC3F7', mpesa_stk: '#10B981', mpesa_till: '#2E8B57',
  card: '#6D5EBD', wallet: '#E5A566', store_credit: '#8BA4C4', bank_transfer: '#2AA9AB',
};

export default {
  name: 'Dashboard',
  components: { Navigation },
  data() {
    return {
      kpi: null, onboarding: null,
      userName: sessionStorage.getItem('userName') || 'there',
    };
  },
  computed: {
    isRestaurantMode() { const bt = sessionStorage.getItem('businessType') || ''; return bt === 'restaurant' || bt === 'mixed'; },
    isServiceMode()    { const bt = sessionStorage.getItem('businessType') || ''; return bt === 'service'    || bt === 'mixed'; },
    trendTotal() { return this.kpi ? this.kpi.trend_7d.reduce((s, d) => s + Number(d.gross || 0), 0) : 0; },
    trendMax()   { return this.kpi ? Math.max(1, ...this.kpi.trend_7d.map(d => Number(d.gross || 0))) : 1; },
    hourMax()    { return this.kpi ? Math.max(1, ...this.kpi.by_hour) : 1; },
    methodTotal(){ return this.kpi ? this.kpi.by_method.reduce((s, m) => s + Number(m.total || 0), 0) : 0; },
  },
  async mounted() { await Promise.all([this.load(), this.loadOnboarding()]); },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    formatK(n) { n = Number(n); return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : Math.round(n); },
    prettyDate() { return new Date().toLocaleDateString('en-KE', { weekday: 'long', month: 'long', day: 'numeric' }); },
    prettyMethod(m) { return METHOD_LABEL[m] || m; },
    methodColor(m) { return METHOD_COLOR[m] || 'var(--paysol-teal)'; },
    methodPct(v) { return this.methodTotal > 0 ? Math.max(4, (Number(v) / this.methodTotal) * 100) : 0; },
    barHeight(v) { return Math.max(2, (Number(v) / this.trendMax) * 100); },
    hourHeight(v) { return Math.max(2, (Number(v) / this.hourMax) * 100); },
    shortDay(iso) { return new Date(iso + 'T00:00:00').toLocaleDateString('en-KE', { weekday: 'short' }); },
    shortTime(iso) {
      const d = new Date(iso.replace(' ', 'T'));
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      if (sameDay) return d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
      return d.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' });
    },
    wizLink(k) {
      if (k === 'products') return '/products';
      if (k === 'staff') return '/users';
      if (k === 'mpesa') return '/settings';
      if (k === 'sms') return '/settings';
      if (k === 'sale') return '/pos';
      return '/dashboard';
    },
    async load() {
      const { data } = await execute({ function: 'getDashboardKpisV2' }, paysol);
      if (data.success) this.kpi = data.data;
    },
    async loadOnboarding() {
      try {
        const { data } = await execute({ function: 'getOnboardingStatus' }, paysol);
        if (data.success) this.onboarding = data.data;
      } catch (e) { /* silent */ }
    },
    async dismissWiz() {
      const { data } = await execute({ function: 'dismissOnboarding' }, paysol);
      if (data.success) this.onboarding.dismissed = true;
    },
    async finishWiz() {
      const { data } = await execute({ function: 'completeOnboarding' }, paysol);
      if (data.success) { this.onboarding.completed_at = new Date().toISOString(); this.onboarding.dismissed = true; }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1400px; margin: 0 auto; padding: 24px; }
.loader { max-width: 1200px; margin: 60px auto; padding: 24px; color: var(--paysol-muted); }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 16px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }

/* Wizard */
.wizard { padding: 20px 24px; margin-bottom: 20px; background: linear-gradient(120deg, var(--paysol-bg-elev), var(--paysol-card)); border-left: 3px solid var(--paysol-teal); }
.wiz-head { display: flex; justify-content: space-between; align-items: start; }
.wiz-eyebrow { color: var(--paysol-teal); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
.wiz-head h3 { margin: 4px 0; font-size: 20px; }
.wiz-head p { margin: 0; }
.wiz-progress { display: flex; align-items: center; gap: 10px; margin: 12px 0 16px; }
.wiz-bar { flex: 1; height: 6px; background: var(--paysol-line); border-radius: 3px; overflow: hidden; }
.wiz-fill { height: 100%; background: var(--paysol-teal); transition: width 0.3s; }
.wiz-progress-text { color: var(--paysol-muted); font-size: 12px; white-space: nowrap; }
.wiz-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
.wiz-step { display: flex; align-items: start; gap: 10px; padding: 10px 12px; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 8px; }
.wiz-step.done { border-color: var(--paysol-success); background: rgba(16,185,129,.05); }
.wiz-step.optional .wiz-step-title { color: var(--paysol-muted); }
.wiz-check i { font-size: 22px; color: var(--paysol-muted); }
.wiz-step.done .wiz-check i { color: var(--paysol-success); }
.wiz-step-body { flex: 1; min-width: 0; }
.wiz-step-title { font-size: 13px; font-weight: 600; color: var(--paysol-text); margin-bottom: 3px; }
.wiz-opt { color: var(--paysol-muted); font-size: 10px; font-weight: 500; margin-left: 6px; text-transform: lowercase; }
.wiz-step-cta a { color: var(--paysol-teal); font-size: 12px; text-decoration: none; font-weight: 600; }
.wiz-step-cta a:hover { text-decoration: underline; }
.wiz-step-done { color: var(--paysol-success); font-size: 11px; font-weight: 600; }
.wiz-actions { margin-top: 16px; text-align: right; }

/* KPI tiles */
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 16px; }
.kpi { padding: 16px 18px; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-left: 3px solid var(--paysol-muted); border-radius: 8px; }
.kpi.teal { border-left-color: var(--paysol-teal); }
.kpi.warn { border-left-color: var(--paysol-warning); }
.kpi-label { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.kpi-val { color: var(--paysol-text); font-size: 24px; font-weight: 700; margin-top: 4px; }
.kpi-sub { color: var(--paysol-muted); font-size: 11px; margin-top: 4px; }

/* Quick actions */
.quick-row { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
.quick-row button { display: inline-flex; align-items: center; gap: 6px; }
.quick-row button i { font-size: 16px; }

/* Trend */
.trend-card { margin-bottom: 16px; }
.section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.section-head h3 { color: var(--paysol-text); font-size: 15px; margin: 0; font-weight: 700; }
.trend-chart { display: flex; align-items: end; gap: 8px; height: 140px; }
.bar-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.bar-wrap { flex: 1; width: 100%; display: flex; align-items: end; justify-content: center; }
.bar { width: 70%; background: linear-gradient(180deg, var(--paysol-teal), var(--paysol-teal-deep)); border-radius: 3px 3px 0 0; transition: height 0.3s; min-height: 3px; }
.bar-label { color: var(--paysol-muted); font-size: 11px; font-weight: 600; }
.bar-value { font-size: 10px; }

/* 2-col grid */
.two-col { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 16px; }

/* Payment method bars */
.method-list { display: flex; flex-direction: column; gap: 10px; }
.method-row { display: grid; grid-template-columns: 100px 1fr 110px; gap: 10px; align-items: center; }
.method-name { color: var(--paysol-text); font-size: 13px; font-weight: 500; }
.method-bar-wrap { height: 10px; background: var(--paysol-bg-elev); border-radius: 5px; overflow: hidden; }
.method-bar { height: 100%; border-radius: 5px; }
.method-val { text-align: right; color: var(--paysol-text); font-weight: 600; font-size: 13px; }

/* Hour chart */
.hour-chart { display: flex; align-items: end; gap: 3px; height: 100px; }
.hour-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.hour-bar-wrap { flex: 1; width: 100%; display: flex; align-items: end; justify-content: center; }
.hour-bar { width: 100%; background: var(--paysol-teal); border-radius: 2px 2px 0 0; min-height: 2px; }
.hour-label { font-size: 9px; }

/* Mini tables */
.mini-table { font-size: 12px; }
.mini-table th, .mini-table td { padding: 6px 10px; }
.mini-table th { font-size: 10px; }

.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 30px 0; }
.r { text-align: right; }
.warn { color: var(--paysol-warning); }
code { background: var(--paysol-bg-elev); padding: 2px 6px; border-radius: 3px; color: var(--paysol-teal); font-size: 11px; }
button.small { padding: 4px 10px; font-size: 11px; }
</style>
